/**
 * Web Crypto API AES-256-GCM Vault Encryption Engine
 * Zero external cost, client-side zero-knowledge encryption for financial data and sensitive payloads.
 */

const SALT = new TextEncoder().encode('azuraos_vault_secure_salt_2026');

async function getEncryptionKey(passphrase: string): Promise<CryptoKey> {
  const enc = new TextEncoder();
  const keyMaterial = await window.crypto.subtle.importKey(
    'raw',
    enc.encode(passphrase),
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );

  return window.crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: SALT,
      iterations: 100000,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Encrypts a plain text string into a AES-256-GCM base64 string
 */
export async function encryptPayload(text: string, secretKey: string = 'azuraos-default-master-key'): Promise<string> {
  try {
    const enc = new TextEncoder();
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const key = await getEncryptionKey(secretKey);
    const encryptedContent = await window.crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv,
      },
      key,
      enc.encode(text)
    );

    const buffer = new Uint8Array(iv.byteLength + encryptedContent.byteLength);
    buffer.set(iv, 0);
    buffer.set(new Uint8Array(encryptedContent), iv.byteLength);

    // Return as base64 with prefix tag
    const base64Str = btoa(String.fromCharCode(...buffer));
    return `ENC_AES256:${base64Str}`;
  } catch (err) {
    console.error('Encryption failed:', err);
    throw new Error('Encryption process failed.');
  }
}

/**
 * Decrypts an AES-256-GCM encrypted payload back to plain text
 */
export async function decryptPayload(cipherText: string, secretKey: string = 'azuraos-default-master-key'): Promise<string> {
  if (!cipherText || !cipherText.startsWith('ENC_AES256:')) {
    return cipherText; // Not encrypted
  }

  try {
    const rawBase64 = cipherText.replace('ENC_AES256:', '');
    const binaryStr = atob(rawBase64);
    const bytes = new Uint8Array(binaryStr.length);
    for (let i = 0; i < binaryStr.length; i++) {
      bytes[i] = binaryStr.charCodeAt(i);
    }

    const iv = bytes.slice(0, 12);
    const data = bytes.slice(12);

    const key = await getEncryptionKey(secretKey);
    const decryptedContent = await window.crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv,
      },
      key,
      data
    );

    const dec = new TextDecoder();
    return dec.decode(decryptedContent);
  } catch (err) {
    console.error('Decryption failed or key mismatch:', err);
    throw new Error('Decryption failed. Integrity error or invalid key.');
  }
}

/**
 * Generates SHA-256 HMAC / Digest signature of payload for tampering & intrusion detection
 */
export async function generateSHA256Signature(data: string): Promise<string> {
  const enc = new TextEncoder();
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', enc.encode(data));
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Checks if string is encrypted
 */
export function isEncrypted(val: string): boolean {
  return typeof val === 'string' && val.startsWith('ENC_AES256:');
}
